/**
 * RoundupEditor — single-page sectioned form for creating / editing standalone
 * round-up emails.  Rebuilt from the original 4-step Stepper to match the
 * CommScheduleDrawer pattern (sticky header, scrollable body, sticky footer).
 *
 * New capabilities vs. the old editor:
 *  • Flexible recurrence (daily / weekly / monthly / interval / yearly)
 *  • Check-category filter (All / Safety / Delivery / Loan)
 *  • asset_tag scope via shared ScopePicker
 *  • Shared RecipientPicker (inline — same as CommScheduleDrawer)
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Dialog, Typography, Button, TextField,
  FormControl, InputLabel, Select, MenuItem,
  Chip, Stack, Alert, CircularProgress,
  IconButton, Divider, Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Repeat as RepeatIcon,
  Person as PersonIcon,
  Group as TeamIcon,
  LocalOffer as TagIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import ScopePicker, {
  ScopeType,
  ScopeLocation,
  ScopeAssetType,
  ScopeAsset,
  ScopeJobTag,
  ScopeTemplate,
} from './shared/ScopePicker';
import { CommSectionLabel } from './shared';
import { CATEGORY_META } from './shared/communicationsStyles';

// ─── Auth ─────────────────────────────────────────────────────────────────────

const getToken = () => localStorage.getItem('token') ?? '';

// ─── Types ────────────────────────────────────────────────────────────────────

type RecurrenceType = 'daily' | 'weekly' | 'monthly_date' | 'monthly_weekday' | 'interval' | 'yearly';
type CheckCategory  = 'all' | 'safety' | 'delivery' | 'loan';

interface User    { user_id: string; first_name: string; last_name: string; email: string; }
interface Team    { group_id: string; group_name: string; }
interface JobTag  { job_tag_id: string; tag_name: string; tag_scope?: string; }

interface Recipient {
  recipient_type: 'user' | 'team' | 'job_tag' | 'email';
  user_id?: string;
  team_id?: string;
  job_tag_id?: string;
  email?: string;
  _label?: string;
}

interface Props {
  open: boolean;
  roundup: any | null;
  isNew: boolean;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
  locationsEnabled?: boolean;
  locationLabel?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TZ = 'Europe/London';

const DAY_LABELS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const RECURRENCE_OPTS: { value: RecurrenceType; label: string; desc: string }[] = [
  { value: 'daily',           label: 'Daily',             desc: 'Every day' },
  { value: 'weekly',          label: 'Weekly',            desc: 'Choose specific days' },
  { value: 'monthly_date',    label: 'Monthly (date)',    desc: 'e.g. 1st of every month' },
  { value: 'monthly_weekday', label: 'Monthly (weekday)', desc: 'e.g. first Monday' },
  { value: 'interval',        label: 'Every N',           desc: 'e.g. every 2 weeks' },
  { value: 'yearly',          label: 'Yearly',            desc: 'Once a year' },
];

const CHECK_CATEGORIES: { value: CheckCategory; label: string; desc: string }[] = [
  { value: 'all',      label: 'All Checks',      desc: 'Include every check type' },
  { value: 'safety',   label: 'Safety Checks',   desc: CATEGORY_META.safety.subtitle },
  { value: 'delivery', label: 'Delivery Checks',  desc: CATEGORY_META.delivery.subtitle },
  { value: 'loan',     label: 'Loan Checks',      desc: CATEGORY_META.loan.subtitle },
];

function ordinal(n: number): string {
  if (n === -1) return 'Last';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function describeRecurrence(form: {
  recurrence_type: RecurrenceType;
  standalone_days_of_week: number[];
  standalone_day_of_month: number;
  standalone_week_of_month: number;
  standalone_weekday: number;
  standalone_interval_amount: number;
  standalone_interval_unit: string;
  standalone_month_of_year: number;
  standalone_yearly_day: number;
}): string {
  switch (form.recurrence_type) {
    case 'daily': return 'Every day';
    case 'weekly': {
      const days = form.standalone_days_of_week.map(d => DAY_LABELS[d]).join(', ');
      return days || 'Weekly';
    }
    case 'monthly_date':
      return form.standalone_day_of_month === -1
        ? 'Last day of every month'
        : `${ordinal(form.standalone_day_of_month)} of every month`;
    case 'monthly_weekday': {
      const ord: Record<number, string> = { 1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', [-1]: 'Last' };
      return `${ord[form.standalone_week_of_month] ?? 'First'} ${FULL_DAY_NAMES[form.standalone_weekday]} of every month`;
    }
    case 'interval':
      return `Every ${form.standalone_interval_amount} ${form.standalone_interval_unit}`;
    case 'yearly':
      return `${MONTH_NAMES_SHORT[form.standalone_month_of_year]} ${ordinal(form.standalone_yearly_day)} each year`;
    default:
      return 'Weekly';
  }
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Inline recipient picker (mirrors CommScheduleDrawer's version) ────────────

function labelFor(r: Recipient): string {
  return r._label ?? r.email ?? (r.user_id ? 'User' : r.team_id ? 'Team' : 'Tag');
}

const InlineRecipientPicker: React.FC<{
  recipients: Recipient[];
  onChange: (recs: Recipient[]) => void;
  users: User[];
  teams: Team[];
  jobTags: JobTag[];
}> = ({ recipients, onChange, users, teams, jobTags }) => {
  const [tab, setTab] = useState<'user' | 'team' | 'job_tag' | 'email'>('user');
  const [freeEmail, setFreeEmail] = useState('');

  const addUser = (u: User | null) => {
    if (!u || recipients.some(r => r.user_id === u.user_id)) return;
    onChange([...recipients, { recipient_type: 'user', user_id: u.user_id, _label: `${u.first_name} ${u.last_name}`.trim() || u.email }]);
  };
  const addTeam = (t: Team | null) => {
    if (!t || recipients.some(r => r.team_id === t.group_id)) return;
    onChange([...recipients, { recipient_type: 'team', team_id: t.group_id, _label: t.group_name }]);
  };
  const addTag = (j: JobTag | null) => {
    if (!j || recipients.some(r => r.job_tag_id === j.job_tag_id)) return;
    onChange([...recipients, { recipient_type: 'job_tag', job_tag_id: j.job_tag_id, _label: j.tag_name }]);
  };
  const addEmail = () => {
    const v = freeEmail.trim().toLowerCase();
    if (!v || !EMAIL_RX.test(v) || recipients.some(r => r.email?.toLowerCase() === v)) return;
    onChange([...recipients, { recipient_type: 'email', email: v, _label: v }]);
    setFreeEmail('');
  };
  const remove = (idx: number) => onChange(recipients.filter((_, i) => i !== idx));

  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {(['user', 'team', 'job_tag', 'email'] as const).map(t => (
          <Chip
            key={t}
            icon={
              t === 'user'    ? <PersonIcon sx={{ fontSize: 14 }} />
              : t === 'team'  ? <TeamIcon   sx={{ fontSize: 14 }} />
              : t === 'job_tag' ? <TagIcon  sx={{ fontSize: 14 }} />
              : <EmailIcon sx={{ fontSize: 14 }} />
            }
            label={t === 'user' ? 'User' : t === 'team' ? 'Team' : t === 'job_tag' ? 'Tag' : 'Email'}
            size="small"
            variant={tab === t ? 'filled' : 'outlined'}
            color={tab === t ? 'primary' : 'default'}
            onClick={() => setTab(t)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      <Box sx={{ mb: 1.5 }}>
        {tab === 'user' && (
          <Autocomplete
            options={users}
            getOptionLabel={u => `${u.first_name} ${u.last_name} (${u.email})`}
            onChange={(_, v) => addUser(v)}
            value={null}
            size="small"
            renderInput={p => <TextField {...p} label="Add a user" />}
          />
        )}
        {tab === 'team' && (
          <Autocomplete
            options={teams}
            getOptionLabel={t => t.group_name}
            onChange={(_, v) => addTeam(v)}
            value={null}
            size="small"
            renderInput={p => <TextField {...p} label="Add a team" />}
          />
        )}
        {tab === 'job_tag' && (
          <Autocomplete
            options={jobTags}
            getOptionLabel={j => j.tag_name}
            onChange={(_, v) => addTag(v)}
            value={null}
            size="small"
            renderInput={p => <TextField {...p} label="Add a tag" />}
          />
        )}
        {tab === 'email' && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Email address"
              type="email"
              value={freeEmail}
              onChange={e => setFreeEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addEmail()}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addEmail}>Add</Button>
          </Stack>
        )}
      </Box>

      {recipients.length === 0 ? (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          No recipients added yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {recipients.map((r, i) => (
            <Chip
              key={i}
              label={labelFor(r)}
              size="small"
              variant="outlined"
              color={r.recipient_type === 'user' ? 'primary' : r.recipient_type === 'team' ? 'secondary' : 'default'}
              onDelete={() => remove(i)}
              deleteIcon={<DeleteIcon />}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode; sx?: object }> = ({ title, children, sx }) => (
  <Box sx={{ mb: 3, ...sx }}>
    <CommSectionLabel>{title}</CommSectionLabel>
    {children}
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const RoundupEditor: React.FC<Props> = ({
  open, roundup, isNew, onSave, onClose, locationsEnabled = true, locationLabel = 'Location',
}) => {
  // ── Form state ──────────────────────────────────────────────────────────────
  const [name, setName]             = useState('');
  const [checkCategory, setCheckCategory] = useState<CheckCategory>('all');
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('weekly');
  const [daysOfWeek, setDaysOfWeek]         = useState<number[]>([1, 2, 3, 4, 5]);
  const [dayOfMonth, setDayOfMonth]         = useState(1);
  const [weekOfMonth, setWeekOfMonth]       = useState(1);
  const [weekday, setWeekday]               = useState(1);
  const [intervalAmount, setIntervalAmount] = useState(2);
  const [intervalUnit, setIntervalUnit]     = useState<'days' | 'weeks' | 'months'>('weeks');
  const [monthOfYear, setMonthOfYear]       = useState(0);
  const [yearlyDay, setYearlyDay]           = useState(1);
  const [timeOfDay, setTimeOfDay]           = useState('08:00');
  const [lookback, setLookback]             = useState(24);
  const [lookbackUnit, setLookbackUnit]     = useState<'hours' | 'days' | 'weeks' | 'months'>('hours');

  const [scopeType, setScopeType]           = useState<ScopeType>('all');
  const [locationId, setLocationId]         = useState<string | null>(null);
  const [assetTypeIds, setAssetTypeIds]     = useState<string[]>([]);
  const [assetIds, setAssetIds]             = useState<string[]>([]);
  const [jobTagIds, setJobTagIds]           = useState<string[]>([]);
  const [tagTemplateIds, setTagTemplateIds] = useState<string[]>([]);
  const [scopeTemplateId, setScopeTemplateId]         = useState('');
  const [scopeTemplateAssetId, setScopeTemplateAssetId] = useState('');

  const [recipients, setRecipients]         = useState<Recipient[]>([]);

  // ── Reference data ──────────────────────────────────────────────────────────
  const [locations, setLocations]     = useState<ScopeLocation[]>([]);
  const [assetTypes, setAssetTypes]   = useState<ScopeAssetType[]>([]);
  const [assets, setAssets]           = useState<ScopeAsset[]>([]);
  const [users, setUsers]             = useState<User[]>([]);
  const [teams, setTeams]             = useState<Team[]>([]);
  const [jobTags, setJobTags]         = useState<ScopeJobTag[]>([]);
  const [templates, setTemplates]     = useState<ScopeTemplate[]>([]);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [saving, setSaving]               = useState(false);
  const [testing, setTesting]             = useState(false);
  const [loadingFull, setLoadingFull]     = useState(false);
  const [error, setError]                 = useState('');
  const [statusMsg, setStatusMsg]         = useState('');
  const [statusSev, setStatusSev]         = useState<'success' | 'error'>('success');
  const [testEmail, setTestEmail]         = useState('');

  // ── Load reference data once ─────────────────────────────────────────────────
  useEffect(() => {
    const h = { Authorization: `Bearer ${getToken()}` };
    Promise.allSettled([
      axios.get(`${API_BASE_URL}/locations`, { headers: h }),
      axios.get(`${API_BASE_URL}/asset-types`, { headers: h }),
      axios.get(`${API_BASE_URL}/assets`, { headers: h }),
      axios.get(`${API_BASE_URL}/users`, { headers: h }),
      axios.get(`${API_BASE_URL}/teams`, { headers: h }),
      axios.get(`${API_BASE_URL}/job-tags`, { headers: h }),
      axios.get(`${API_BASE_URL}/check-templates`, { headers: h }),
    ]).then(([locR, atR, asR, uR, tR, jR, tmplR]) => {
      const unwrap = (r: PromiseSettledResult<any>) => {
        if (r.status !== 'fulfilled') return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.data ?? d?.users ?? d?.groups ?? [];
      };
      setLocations(unwrap(locR));
      setAssetTypes(unwrap(atR));
      setAssets(unwrap(asR));
      setUsers(unwrap(uR).map((u: any) => ({
        user_id: u.user_id ?? u.userId ?? '',
        first_name: u.first_name ?? u.firstName ?? '',
        last_name: u.last_name ?? u.lastName ?? '',
        email: u.email ?? '',
      })));
      setTeams(unwrap(tR).map((t: any) => ({
        group_id: t.group_id ?? t.groupId ?? '',
        group_name: t.group_name ?? t.groupName ?? '',
      })));
      setJobTags(unwrap(jR));
      setTemplates(unwrap(tmplR));
    });
  }, []);

  // ── Reset / seed form ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setError(''); setStatusMsg(''); setTestEmail('');
    if (isNew || !roundup) {
      setName(''); setCheckCategory('all');
      setRecurrenceType('weekly'); setDaysOfWeek([1, 2, 3, 4, 5]);
      setDayOfMonth(1); setWeekOfMonth(1); setWeekday(1);
      setIntervalAmount(2); setIntervalUnit('weeks');
      setMonthOfYear(0); setYearlyDay(1);
      setTimeOfDay('08:00'); setLookback(24); setLookbackUnit('hours');
      setScopeType('all'); setLocationId(null);
      setAssetTypeIds([]); setAssetIds([]); setJobTagIds([]); setTagTemplateIds([]);
      setScopeTemplateId(''); setScopeTemplateAssetId('');
      setRecipients([]);
      return;
    }
    setLoadingFull(true);
    axios.get(`${API_BASE_URL}/communication-roundups/${roundup.roundup_id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(res => {
      const d = res.data?.data ?? res.data;
      if (!d) return;
      setName(d.name ?? '');
      setCheckCategory(d.check_category ?? 'all');
      setRecurrenceType(d.standalone_recurrence_type ?? 'weekly');
      setDaysOfWeek(d.standalone_days_of_week ?? [1, 2, 3, 4, 5]);
      setDayOfMonth(d.standalone_day_of_month ?? 1);
      setWeekOfMonth(d.standalone_week_of_month ?? 1);
      setWeekday(d.standalone_weekday ?? 1);
      setIntervalAmount(d.standalone_interval_amount ?? 2);
      setIntervalUnit(d.standalone_interval_unit ?? 'weeks');
      setMonthOfYear(d.standalone_month_of_year ?? 0);
      setYearlyDay(d.standalone_yearly_day ?? 1);
      setTimeOfDay(d.standalone_time_of_day?.slice(0, 5) ?? '08:00');
      setLookback(d.standalone_lookback_hours ?? 24);
      setLookbackUnit(d.standalone_lookback_unit ?? 'hours');
      const st = d.scope_type ?? 'all';
      setScopeType((st === 'location' && !locationsEnabled) ? 'all' : st);
      setLocationId(d.scope_location_id ?? null);
      setAssetTypeIds(d.scope_asset_type_ids ?? []);
      setAssetIds(d.scope_asset_ids ?? []);
      setJobTagIds(d.scope_job_tag_ids ?? []);
      setTagTemplateIds(d.scope_tag_template_ids ?? []);
      setScopeTemplateId(d.scope_template_id ?? '');
      setScopeTemplateAssetId(d.scope_template_asset_id ?? '');
      if (Array.isArray(d.recipients)) {
        setRecipients(d.recipients.map((r: any) => ({
          ...r,
          _label: r.user_id
            ? (`${r.first_name ?? r.firstName ?? ''} ${r.last_name ?? r.lastName ?? ''}`).trim() || r.email
            : r.team_id ? r.team_name
            : r.job_tag_id ? r.job_tag_name
            : r.email,
        })));
      }
    }).catch(() => {}).finally(() => setLoadingFull(false));
  }, [open, isNew, roundup, locationsEnabled]);

  // ── Save / test ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      await onSave({
        name,
        trigger_type: 'standalone',
        schedule_id: null,
        alert_level_id: null,
        standalone_time_of_day: timeOfDay,
        standalone_days_of_week: daysOfWeek,
        standalone_timezone: TZ,
        standalone_lookback_hours: lookback,
        standalone_lookback_unit: lookbackUnit,
        standalone_recurrence_type: recurrenceType,
        standalone_day_of_month: dayOfMonth,
        standalone_week_of_month: weekOfMonth,
        standalone_weekday: weekday,
        standalone_interval_amount: intervalAmount,
        standalone_interval_unit: intervalUnit,
        standalone_month_of_year: monthOfYear,
        standalone_yearly_day: yearlyDay,
        check_category: checkCategory,
        email_template_id: null,
        email_filter_mode: 'all',
        send_push_notification: false,
        scope_type: scopeType,
        scope_location_id: locationId,
        scope_asset_type_ids: assetTypeIds.length > 0 ? assetTypeIds : null,
        scope_asset_ids: assetIds.length > 0 ? assetIds : null,
        scope_job_tag_ids: jobTagIds.length > 0 ? jobTagIds : null,
        scope_tag_template_ids: (scopeType === 'asset_tag' && tagTemplateIds.length > 0) ? tagTemplateIds : null,
        scope_template_id: (scopeType === 'template' || scopeType === 'asset_template') ? scopeTemplateId || null : null,
        scope_template_asset_id: scopeType === 'asset_template' ? scopeTemplateAssetId || null : null,
        enabled: true,
        recipients: recipients.map(({ _label, ...r }) => r),
      });
    } catch {
      setError('Failed to save round-up. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!roundup?.roundup_id) return;
    setTesting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/communication-roundups/${roundup.roundup_id}/test`,
        testEmail ? { recipient_email: testEmail } : {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setStatusMsg(`Test email sent to ${res.data?.sent_to ?? 'your account email'}.`);
      setStatusSev('success');
    } catch {
      setStatusMsg('Failed to send test email.');
      setStatusSev('error');
    } finally {
      setTesting(false);
    }
  };

  const recurrenceDesc = describeRecurrence({
    recurrence_type: recurrenceType,
    standalone_days_of_week: daysOfWeek,
    standalone_day_of_month: dayOfMonth,
    standalone_week_of_month: weekOfMonth,
    standalone_weekday: weekday,
    standalone_interval_amount: intervalAmount,
    standalone_interval_unit: intervalUnit,
    standalone_month_of_year: monthOfYear,
    standalone_yearly_day: yearlyDay,
  });

  const canSave = !!name && !!timeOfDay && (recurrenceType !== 'weekly' || daysOfWeek.length > 0);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '92vh' } }}
    >
      {/* Sticky header */}
      <Box sx={{
        px: 3, py: 2, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RepeatIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700}>{isNew ? 'New Round-Up' : 'Edit Round-Up'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Standalone summary email · UK time ({TZ})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, position: 'relative' }}>
        {loadingFull && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* ── Name ── */}
        <Section title="Name">
          <TextField
            label="Round-Up Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus={isNew}
            helperText='e.g. "Depot A Morning Summary" or "All Fleet — Weekly Round-Up"'
          />
        </Section>

        <Divider sx={{ mb: 3 }} />

        {/* ── Check category ── */}
        <Section title="Check Category">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Filter this round-up to a specific type of check, or include all checks.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {CHECK_CATEGORIES.map(cat => {
              const active = checkCategory === cat.value;
              const meta   = cat.value !== 'all' ? CATEGORY_META[cat.value as keyof typeof CATEGORY_META] : null;
              return (
                <Box
                  key={cat.value}
                  onClick={() => setCheckCategory(cat.value)}
                  sx={{
                    p: 1.5, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                    borderColor: active ? (meta?.color ?? 'primary.main') : 'divider',
                    bgcolor: active ? (meta?.bg ?? 'primary.50') : 'background.paper',
                    transition: 'border-color .15s, background-color .15s',
                    userSelect: 'none',
                  }}
                >
                  <Typography
                    variant="body2" fontWeight={700}
                    color={active ? (meta?.color ?? 'primary.main') : 'text.primary'}
                    sx={{ mb: 0.25 }}
                  >
                    {cat.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{cat.desc}</Typography>
                </Box>
              );
            })}
          </Box>
        </Section>

        <Divider sx={{ mb: 3 }} />

        {/* ── Recurrence ── */}
        <Section title="Schedule">
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
            Recurrence
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
            {RECURRENCE_OPTS.map(opt => {
              const active = recurrenceType === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setRecurrenceType(opt.value)}
                  sx={{
                    p: 1.25, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'primary.50' : 'background.paper',
                    transition: 'border-color .15s, background-color .15s',
                    userSelect: 'none',
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color={active ? 'primary.main' : 'text.primary'} sx={{ display: 'block' }}>
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {opt.desc}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {recurrenceType === 'weekly' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
                Which days *
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {DAY_LABELS.map((d, i) => (
                  <Chip
                    key={d} label={d}
                    onClick={() => setDaysOfWeek(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}
                    color={daysOfWeek.includes(i) ? 'primary' : 'default'}
                    variant={daysOfWeek.includes(i) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', minWidth: 46 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {recurrenceType === 'monthly_date' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Day of month</InputLabel>
                <Select value={dayOfMonth} label="Day of month" onChange={e => setDayOfMonth(Number(e.target.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <MenuItem key={d} value={d}>{ordinal(d)} of the month</MenuItem>
                  ))}
                  <MenuItem value={-1}>Last day of the month</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {recurrenceType === 'monthly_weekday' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Which occurrence</InputLabel>
                <Select value={weekOfMonth} label="Which occurrence" onChange={e => setWeekOfMonth(Number(e.target.value))}>
                  <MenuItem value={1}>First</MenuItem>
                  <MenuItem value={2}>Second</MenuItem>
                  <MenuItem value={3}>Third</MenuItem>
                  <MenuItem value={4}>Fourth</MenuItem>
                  <MenuItem value={-1}>Last</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Day of week</InputLabel>
                <Select value={weekday} label="Day of week" onChange={e => setWeekday(Number(e.target.value))}>
                  {FULL_DAY_NAMES.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {recurrenceType === 'interval' && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Every</Typography>
              <TextField
                size="small" type="number"
                value={intervalAmount}
                onChange={e => setIntervalAmount(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{ width: 80 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={intervalUnit} onChange={e => setIntervalUnit(e.target.value as typeof intervalUnit)}>
                  <MenuItem value="days">days</MenuItem>
                  <MenuItem value="weeks">weeks</MenuItem>
                  <MenuItem value="months">months</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {recurrenceType === 'yearly' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Month</InputLabel>
                <Select value={monthOfYear} label="Month" onChange={e => setMonthOfYear(Number(e.target.value))}>
                  {MONTH_NAMES_FULL.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px' }}>
                <InputLabel>Day of month</InputLabel>
                <Select value={yearlyDay} label="Day of month" onChange={e => setYearlyDay(Number(e.target.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <MenuItem key={d} value={d}>{ordinal(d)}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Time + look-back */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <TextField
                label="Send at"
                type="time"
                value={timeOfDay}
                onChange={e => setTimeOfDay(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ width: 150 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                UK time — BST/GMT.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Look back"
                type="number"
                value={lookback}
                onChange={e => setLookback(Math.max(1, Number(e.target.value)))}
                inputProps={{ min: 1 }}
                size="small"
                sx={{ width: 90 }}
              />
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Unit</InputLabel>
                <Select value={lookbackUnit} label="Unit" onChange={e => setLookbackUnit(e.target.value as typeof lookbackUnit)}>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Fires at <strong>{timeOfDay}</strong> · <strong>{recurrenceDesc}</strong> — includes the last <strong>{lookback} {lookbackUnit}</strong> of{' '}
            {checkCategory === 'all' ? 'all check' : CHECK_CATEGORIES.find(c => c.value === checkCategory)?.label ?? 'check'} results.
          </Alert>
        </Section>

        <Divider sx={{ mb: 3 }} />

        {/* ── Scope ── */}
        <Section title="Asset Scope">
          <ScopePicker
            scope={scopeType}
            onScopeChange={setScopeType}
            locationId={locationId}
            onLocationChange={setLocationId}
            assetTypeIds={assetTypeIds}
            onAssetTypeChange={setAssetTypeIds}
            assetIds={assetIds}
            onAssetChange={setAssetIds}
            jobTagIds={jobTagIds}
            onJobTagChange={setJobTagIds}
            tagTemplateIds={tagTemplateIds}
            onTagTemplateChange={setTagTemplateIds}
            templateId={scopeTemplateId}
            onTemplateIdChange={setScopeTemplateId}
            templateAssetId={scopeTemplateAssetId}
            onTemplateAssetIdChange={setScopeTemplateAssetId}
            locations={locations}
            assetTypes={assetTypes}
            assets={assets}
            jobTags={jobTags}
            templates={templates}
            locationsEnabled={locationsEnabled}
            locationLabel={locationLabel}
            showTagScope
            showTemplateScope
            showTypeInLocationScope={false}
          />
        </Section>

        <Divider sx={{ mb: 3 }} />

        {/* ── Recipients ── */}
        <Section title="Recipients">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Who should receive this round-up email?
          </Typography>
          <InlineRecipientPicker
            recipients={recipients}
            onChange={setRecipients}
            users={users}
            teams={teams}
            jobTags={jobTags}
          />
        </Section>

        {/* ── Test (edit mode only) ── */}
        {!isNew && roundup?.roundup_id && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Section title="Test">
              {statusMsg && (
                <Alert severity={statusSev} onClose={() => setStatusMsg('')} sx={{ mb: 1.5 }}>
                  {statusMsg}
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="Send to (leave blank for your account email)"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  type="email"
                  sx={{ minWidth: 280 }}
                />
                <Button variant="outlined" startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />} onClick={handleTest} disabled={testing}>
                  {testing ? 'Sending…' : 'Send Test Email'}
                </Button>
              </Stack>
            </Section>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>

      {/* Sticky footer */}
      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : isNew ? 'Create Round-Up' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RoundupEditor;
